import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import {
  Lock, Ticket, Zap, Shield, Users, Gift,
  CheckCircle, AlertCircle, Copy, Flame,
  TrendingUp, ExternalLink, Star, Trophy, Share2, Award
} from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useDemo } from '../context/DemoContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══ LOCKED AIRDROP ═══
const LockedAirdrop = () => {
  const LockedPage = require('../components/LockedPage').default;
  return (
    <LockedPage
      title="THE FLOOD"
      subtitle="Play the arcade → top the monthly board → get paid in real SOL."
      lore="No allocation. No presale. When $GFG bonds on Pump.fun, creator rewards accrue in SOL to the public dev wallet. Each month, 40% of that SOL is paid out to top Descent + arcade players. Play free, climb the board, get real SOL — receipted on the Fair Launch page."
      statusLabel="Sewer Frequency Detected"
      tickerText="The gremlins are gathering..."
      theme={{ primary: '#38bdf8', glow: '#0ea5e9', bg: '#020810' }}
      particles="orbs"
      features={[
        { icon: Gift, title: 'Real SOL', desc: '40% of Pump.fun creator rewards paid monthly to top players' },
        { icon: Shield, title: 'No Wallet Needed', desc: 'Play the demo now; connect later to claim' },
        { icon: Zap, title: 'Skill Not Bags', desc: 'Ranking by Descent clears + arcade fees, not token holdings' },
        { icon: Users, title: 'Casual & Fair', desc: 'No presale, no locked allocation, no team wallets' },
      ]}
      bottomCTA={
        <div className="flex items-center justify-between gap-4 px-5 py-3"
          style={{ background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)' }}>
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4" style={{ color: '#38bdf8' }} />
            <span className="text-[10px] tracking-wider uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(125,190,248,0.7)' }}>Monthly SOL Payouts</span>
            <span className="text-xs font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,190,248,0.45)' }}>Unlocks post-audit</span>
          </div>
          <button onClick={() => window.open('https://discord.gg/3wH7NaVXxP', '_blank')} className="px-4 py-1.5 text-xs font-bold tracking-wider uppercase transition-all hover:scale-105"
            style={{ fontFamily: "'Cinzel', serif", background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8' }}>
            Join the Syndicate
          </button>
        </div>
      }
    />
  );
};

// ═══ PANEL — light blue themed ═══
const Panel = ({ children, className = '', glow = false, style = {}, ...props }) => (
  <div className={`relative overflow-hidden ${className}`} {...props}
    style={{
      background: glow ? 'rgba(4,18,8,0.85)' : 'rgba(3,14,6,0.8)',
      border: `1px solid rgba(56,189,248,${glow ? '0.35' : '0.18'})`,
      backdropFilter: 'blur(10px)',
      boxShadow: glow
        ? '0 0 25px rgba(56,189,248,0.1), inset 0 0 20px rgba(56,189,248,0.04)'
        : '0 0 10px rgba(56,189,248,0.03), inset 0 0 10px rgba(56,189,248,0.02)',
      ...style,
    }}>
    <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l z-10" style={{ borderColor: 'rgba(125,211,252,0.4)' }} />
    <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r z-10" style={{ borderColor: 'rgba(125,211,252,0.4)' }} />
    <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l z-10" style={{ borderColor: 'rgba(125,211,252,0.4)' }} />
    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r z-10" style={{ borderColor: 'rgba(125,211,252,0.4)' }} />
    <motion.div className="absolute inset-0 pointer-events-none"
      style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(56,189,248,0.04) 50%, transparent 60%)', backgroundSize: '300% 300%' }}
      animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
    {children}
  </div>
);

// ═══ TASK CARD ═══
const TaskCard = ({ task, taskId, completed }) => {
  const taskMeta = {
    connect_wallet: { icon: Zap, color: '#7dd3fc' },
    discord_code: { icon: Users, color: '#818cf8' },
    twitter_code: { icon: ExternalLink, color: '#38bdf8' },
    arcade_play: { icon: Trophy, color: '#fbbf24' },
    graveyard_submit: { icon: Flame, color: '#ef4444' },
    referral: { icon: Gift, color: '#38bdf8' },
  };
  const meta = taskMeta[taskId] || { icon: Star, color: '#7dd3fc' };
  const Icon = meta.icon;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden" data-testid={`task-${taskId}`}
      style={{
        background: completed ? 'rgba(56,189,248,0.08)' : 'rgba(3,14,6,0.7)',
        border: `1px solid ${completed ? 'rgba(56,189,248,0.3)' : 'rgba(56,189,248,0.12)'}`,
      }}>
      <div className="flex items-center justify-between p-3.5 relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}30` }}>
            {completed ? <CheckCircle className="w-4 h-4" style={{ color: '#38bdf8' }} /> : <Icon className="w-4 h-4" style={{ color: meta.color }} />}
          </div>
          <div>
            <p className="text-sm font-bold" style={{ fontFamily: "'Cinzel', serif", color: completed ? '#7dd3fc' : 'rgba(180,230,200,0.85)' }}>{task.name}</p>
            <p className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: completed ? 'rgba(56,189,248,0.5)' : 'rgba(125,211,252,0.35)' }}>
              {task.type === 'automatic' ? 'Automatic' : task.type === 'code' ? 'Enter code' : 'Referral'}
            </p>
          </div>
        </div>
        <span className="text-sm font-bold px-2 py-0.5" style={{
          fontFamily: "'Impact', sans-serif",
          color: completed ? '#38bdf8' : '#7dd3fc',
          background: completed ? 'rgba(56,189,248,0.12)' : 'rgba(56,189,248,0.08)',
          border: `1px solid ${completed ? 'rgba(56,189,248,0.25)' : 'rgba(56,189,248,0.15)'}`,
          transform: 'skewX(-8deg)',
        }}>+{task.points}</span>
      </div>
    </motion.div>
  );
};


export default function AirdropPage() {
  const navigate = useNavigate();
  const { publicKey, connected: walletConnected } = useWallet();
  const { isDemo, demoWallet } = useDemo();
  const connected = walletConnected || isDemo;
  const walletAddress = publicKey?.toBase58() || (isDemo ? demoWallet : '');

  const [airdropInfo, setAirdropInfo] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [codeInput, setCodeInput] = useState('');
  const [claimMsg, setClaimMsg] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    try {
      const [infoRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/airdrop/info`),
        walletAddress ? fetch(`${API_URL}/api/airdrop/status/${walletAddress}`) : null,
      ]);
      if (infoRes.ok) setAirdropInfo(await infoRes.json());
      if (statusRes?.ok) setUserStatus(await statusRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [walletAddress]);

  const handleRegister = async (refCode = '') => {
    if (!walletAddress) return;
    setRegistering(true);
    try {
      const params = new URLSearchParams({ wallet_address: walletAddress });
      if (refCode) params.append('referral_code', refCode);
      const res = await fetch(`${API_URL}/api/airdrop/register?${params}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) { setClaimMsg({ type: 'success', text: data.message }); fetchData(); }
      else { setClaimMsg({ type: 'error', text: data.message }); }
    } catch (e) { setClaimMsg({ type: 'error', text: 'Failed to register' }); }
    finally { setRegistering(false); setTimeout(() => setClaimMsg(null), 4000); }
  };

  const handleClaimCode = async () => {
    if (!codeInput.trim() || !walletAddress) return;
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/airdrop/claim-code?wallet_address=${walletAddress}&code=${codeInput.trim()}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) { setClaimMsg({ type: 'success', text: data.message }); setCodeInput(''); fetchData(); }
      else { setClaimMsg({ type: 'error', text: data.message }); }
    } catch (e) { setClaimMsg({ type: 'error', text: 'Failed to claim code' }); }
    finally { setClaiming(false); setTimeout(() => setClaimMsg(null), 4000); }
  };

  const copyReferral = () => {
    if (userStatus?.referral_code) {
      navigator.clipboard.writeText(`${window.location.origin}/airdrop?ref=${userStatus.referral_code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    if (connected && walletAddress && userStatus && !userStatus.registered && airdropInfo?.status === 'accumulating') {
      const params = new URLSearchParams(window.location.search);
      handleRegister(params.get('ref') || '');
    }
  }, [connected, walletAddress, userStatus, airdropInfo]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#020810' }}>
      <motion.div animate={{ opacity: [0.3, 1, 0.3], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 2, repeat: Infinity }}>
        <Gift className="w-10 h-10" style={{ color: '#38bdf8', filter: 'drop-shadow(0 0 20px rgba(56,189,248,0.6))' }} />
      </motion.div>
    </div>
  );
  if (!airdropInfo || airdropInfo.status === 'inactive') return <LockedAirdrop />;

  const demoConnected = connected || isDemo;
  const tasks = airdropInfo?.tasks || {};
  const completedTasks = (userStatus?.completed_tasks || []).map(t => t.task);
  const totalPoints = userStatus?.total_points || 0;
  const isQualified = userStatus?.qualified || false;
  const minPoints = airdropInfo?.min_points || 200;
  const progressPct = Math.min(100, (totalPoints / minPoints) * 100);

  return (
    <div className="min-h-screen relative text-white" data-testid="airdrop-page"
      style={{ background: '#020810' }}>

      <style>{`
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes glowPulse { 0%,100% { box-shadow: 0 0 20px rgba(56,189,248,0.15), 0 0 60px rgba(56,189,248,0.05); } 50% { box-shadow: 0 0 35px rgba(56,189,248,0.25), 0 0 80px rgba(56,189,248,0.1); } }
      `}</style>

      {/* ═══ Background ═══ */}
      <div className="fixed inset-0 z-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #081520 0%, #040b14 40%, #020810 100%)' }} />
      {/* Midjourney background — The Sewer Treasury */}
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <img src="/images/airdrop/sewer_treasury_bg.png" alt=""
          className="w-full h-full object-cover"
          style={{ opacity: 0.13, mixBlendMode: 'screen' }} />
      </div>
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 25%, rgba(56,189,248,0.08) 0%, transparent 50%)' }}
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 4, repeat: Infinity }} />
      </div>

      {/* CRT scanline */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.18) 50%)', backgroundSize: '100% 4px', opacity: 0.1,
      }} />

      {/* Particles — blue orbs */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <motion.div key={`p-${i}`} className="absolute rounded-full"
            style={{
              left: `${4 + i * 8}%`, bottom: '-8px',
              width: `${3 + Math.random() * 2}px`, height: `${3 + Math.random() * 2}px`,
              background: i % 3 === 0 ? '#7dd3fc' : i % 3 === 1 ? '#38bdf8' : '#bae6fd',
              boxShadow: `0 0 ${6 + Math.random() * 6}px rgba(56,189,248,0.7)`,
            }}
            animate={{ y: [0, -700], x: [0, (Math.random() - 0.5) * 30], opacity: [0, 0.7, 0.25, 0] }}
            transition={{ duration: 6 + Math.random() * 4, delay: i * 0.5, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
      </div>

      <BackButton to="/" />

      {/* ═══ CENTERED CONTENT ═══ */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 lg:px-12 pt-20 pb-16">

        {/* ═══ HEADER ═══ */}
        <div className="mb-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="relative">
              <div className="absolute -inset-x-6 -inset-y-3 pointer-events-none" style={{
                background: 'radial-gradient(ellipse at 40% 60%, rgba(56,189,248,0.1) 0%, transparent 70%)', filter: 'blur(12px)',
              }} />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] uppercase tracking-[0.5em] mb-1 relative"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(125,211,252,0.65)' }}>
                {airdropInfo?.status === 'accumulating' ? 'Earning Phase Active' : airdropInfo?.status === 'distributing' ? 'Distribution In Progress' : 'Airdrop Complete'}
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
                className="relative text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider inline-block"
                style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }} data-testid="airdrop-title">
                <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(56,189,248,0.25)', filter: 'blur(6px)' }}>THE FLOOD</span>
                <span className="relative" style={{
                  background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 15%, #7dd3fc 30%, #38bdf8 50%, #0ea5e9 70%, #0284c7 90%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 25px rgba(56,189,248,0.5)) drop-shadow(0 0 50px rgba(56,189,248,0.2))',
                }}>THE FLOOD</span>
                <motion.span className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(209,250,229,0.4) 50%, transparent 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
                  animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>THE FLOOD</motion.span>
              </motion.h1>
            </div>

            {/* Right badges */}
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2"
                style={{ border: '1px solid rgba(56,189,248,0.3)', background: 'rgba(56,189,248,0.06)' }}>
                <motion.div className="w-2 h-2 rounded-full" style={{ background: '#38bdf8', boxShadow: '0 0 8px #38bdf8' }}
                  animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }} />
                <span className="text-xs font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: '#7dd3fc' }}>
                  {airdropInfo?.total_participants || 0} GREMLINS
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2"
                style={{ border: '1px solid rgba(56,189,248,0.35)', background: 'rgba(56,189,248,0.08)', boxShadow: '0 0 12px rgba(56,189,248,0.06)' }}>
                <Gift className="w-4 h-4" style={{ color: '#38bdf8', filter: 'drop-shadow(0 0 4px rgba(56,189,248,0.5))' }} />
                <span className="text-base md:text-lg font-bold tracking-wider" style={{
                  fontFamily: "'Impact', sans-serif",
                  background: 'linear-gradient(180deg, #7dd3fc, #0ea5e9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>Monthly SOL</span>
              </div>
            </div>
          </div>

          {/* Line */}
          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #38bdf8, #7dd3fc, #38bdf8, transparent)' }} />
        </div>

        {/* ═══ POINTS HERO (if connected) ═══ */}
        {demoConnected && userStatus?.registered ? (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Panel glow className="p-6 mb-6" data-testid="points-dashboard">
              <div className="flex items-center justify-between mb-3 relative z-10">
                <div className="flex items-center gap-3">
                  <span className="text-xs tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#7dd3fc' }}>Your Points</span>
                  <span className="text-4xl font-bold" style={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    background: 'linear-gradient(180deg, #e0f2fe, #7dd3fc, #0ea5e9)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 10px rgba(56,189,248,0.3))',
                  }}>{totalPoints}</span>
                </div>
                <div className="flex items-center gap-3">
                  {isQualified ? (
                    <span className="text-[11px] font-bold px-3 py-1.5" style={{ background: 'rgba(56,189,248,0.15)', color: '#7dd3fc', border: '1px solid rgba(56,189,248,0.3)' }}>QUALIFIED</span>
                  ) : (
                    <span className="text-[11px] font-bold px-3 py-1.5" style={{ background: 'rgba(251,191,36,0.08)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.2)' }}>{minPoints - totalPoints} PTS TO QUALIFY</span>
                  )}
                </div>
              </div>
              {/* Progress */}
              <div className="h-4 relative overflow-hidden z-10" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(56,189,248,0.15)' }}>
                <motion.div className="h-full" initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                  style={{
                    background: isQualified ? 'linear-gradient(90deg, #0284c7, #38bdf8, #7dd3fc)' : 'linear-gradient(90deg, #0c4a6e, #0ea5e9, #38bdf8)',
                    boxShadow: '0 0 12px rgba(56,189,248,0.3)',
                    animation: 'glowPulse 3s ease-in-out infinite',
                  }}>
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)', backgroundSize: '200% 100%', animation: 'shimmer 2.5s linear infinite' }} />
                </motion.div>
              </div>
              <div className="flex justify-between mt-1.5 relative z-10">
                <span className="text-[9px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.45)' }}>0 pts</span>
                <span className="text-[9px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.45)' }}>{minPoints} pts (min)</span>
              </div>

              {/* Completed + Referral row */}
              <div className="flex items-center justify-between mt-4 gap-4 relative z-10">
                {(userStatus?.completed_tasks || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {(userStatus.completed_tasks || []).map((t, i) => (
                      <span key={i} className="text-[9px] px-2 py-0.5 font-bold inline-flex items-center gap-1"
                        data-testid={`completed-task-${t.task}-${i}`}
                        style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', color: '#7dd3fc', fontFamily: "'Courier New', monospace" }}>
                        <CheckCircle className="w-2.5 h-2.5" /> +{t.points}
                      </span>
                    ))}
                  </div>
                )}
                {userStatus.referral_code && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Share2 className="w-3.5 h-3.5" style={{ color: 'rgba(125,211,252,0.5)' }} />
                    <code className="text-[10px] truncate max-w-[120px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.5)' }}>{userStatus.referral_code}</code>
                    <button onClick={copyReferral} data-testid="copy-referral" className="p-1 transition-all"
                      style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)' }}>
                      {copied ? <CheckCircle className="w-3 h-3" style={{ color: '#7dd3fc' }} /> : <Copy className="w-3 h-3" style={{ color: 'rgba(125,211,252,0.5)' }} />}
                    </button>
                  </div>
                )}
              </div>
            </Panel>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Panel glow className="p-8 mb-6 text-center" data-testid="connect-prompt">
              <Shield className="w-10 h-10 mx-auto mb-3 relative z-10" style={{ color: 'rgba(56,189,248,0.3)', filter: 'drop-shadow(0 0 10px rgba(56,189,248,0.15))' }} />
              <p className="text-base font-bold mb-1 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(125,211,252,0.7)' }}>Connect Wallet</p>
              <p className="text-xs relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.4)' }}>Connect your Solana wallet to register and start earning points</p>
            </Panel>
          </motion.div>
        )}

        {/* ═══ CODE ENTRY ═══ */}
        {demoConnected && userStatus?.registered && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Panel className="p-4 md:p-5 mb-6" data-testid="code-entry">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 relative z-10">
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold flex-shrink-0" style={{ fontFamily: "'Cinzel', serif", color: '#7dd3fc' }}>Task Code</span>
                <input type="text" value={codeInput} onChange={e => setCodeInput(e.target.value.toUpperCase())}
                  placeholder="SEWER-XXXXXX or TWEET-XXXXXX"
                  className="flex-1 px-4 py-2.5 text-sm outline-none min-w-0"
                  style={{ fontFamily: "'Courier New', monospace", background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(56,189,248,0.15)', color: '#7dd3fc' }}
                  onKeyDown={e => e.key === 'Enter' && handleClaimCode()}
                  data-testid="code-input" />
                <button onClick={handleClaimCode} disabled={claiming || !codeInput.trim()}
                  className="px-6 py-2.5 text-xs font-bold tracking-wider uppercase transition-all flex-shrink-0"
                  style={{ fontFamily: "'Cinzel', serif", background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8', opacity: !codeInput.trim() ? 0.3 : 1 }}
                  data-testid="claim-code-btn">
                  {claiming ? '...' : 'Claim'}
                </button>
              </div>
              <AnimatePresence>
                {claimMsg && (
                  <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="mt-2 flex items-center gap-2 px-3 py-2 text-xs font-bold relative z-10"
                    style={{
                      background: claimMsg.type === 'success' ? 'rgba(56,189,248,0.08)' : 'rgba(239,68,68,0.08)',
                      border: `1px solid ${claimMsg.type === 'success' ? 'rgba(56,189,248,0.25)' : 'rgba(239,68,68,0.25)'}`,
                      color: claimMsg.type === 'success' ? '#38bdf8' : '#ef4444', fontFamily: "'Courier New', monospace",
                    }}>
                    {claimMsg.type === 'success' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                    {claimMsg.text}
                  </motion.div>
                )}
              </AnimatePresence>
            </Panel>
          </motion.div>
        )}

        {/* ═══ TASK BOARD + DISTRIBUTION — responsive ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 mb-8">
          {/* Tasks */}
          <div className="md:col-span-8 w-full" data-testid="task-board">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
              <div className="p-1.5" style={{ background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)' }}>
                <Trophy className="w-4 h-4" style={{ color: '#7dd3fc', filter: 'drop-shadow(0 0 4px rgba(56,189,248,0.4))' }} />
              </div>
              <span className="text-xs tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#7dd3fc' }}>Sewer Tasks</span>
              <span className="text-[10px] font-bold md:ml-auto" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.4)' }}>
                {completedTasks.length}/{Object.keys(tasks).length}
              </span>
            </div>
            <div className="space-y-2">
              {Object.entries(tasks).map(([id, task], i) => (
                <motion.div key={id} transition={{ delay: 0.5 + i * 0.06 }}>
                  <TaskCard task={task} taskId={id} completed={completedTasks.includes(id)} />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Monthly SOL Payout Tiers */}
          <div className="md:col-span-4">
            <Panel className="p-5">
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold block mb-4 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: '#7dd3fc' }}>
                Monthly Payout Tiers
              </span>
              {[
                { rank: 'Rank 1', share: '15%', tone: 'Top of the board' },
                { rank: 'Rank 2-5', share: '15%', tone: 'Podium' },
                { rank: 'Rank 6-25', share: '10%', tone: 'Sewer legends' },
              ].map((t, i) => (
                <div key={i} className="p-3 mb-2 last:mb-0 text-center relative z-10"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(56,189,248,0.12)' }}>
                  <p className="text-[9px] uppercase tracking-wider font-bold mb-1"
                     style={{ fontFamily: "'Cinzel', serif", color: 'rgba(125,211,252,0.5)' }}>{t.rank}</p>
                  <p className="text-xl font-bold"
                     style={{ fontFamily: "'Impact', sans-serif", color: '#38bdf8',
                              filter: 'drop-shadow(0 0 4px rgba(56,189,248,0.25))' }}>
                    {t.share} of pool
                  </p>
                  <p className="text-[9px] mt-0.5"
                     style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.35)' }}>
                    {t.tone}
                  </p>
                </div>
              ))}
              <p className="mt-2 text-[9px] leading-relaxed"
                 style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.4)' }}>
                Pool = 40% of the month&apos;s Pump.fun creator rewards. Paid in SOL. See the{' '}
                <button onClick={() => navigate('/fair-launch')} className="underline" style={{ color: '#7dd3fc' }}>Fair Launch page</button>{' '}for live vault balance.
              </p>
            </Panel>

            {/* How It Works mini */}
            <Panel className="p-4 mt-3">
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold block mb-3 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: '#7dd3fc' }}>How It Works</span>
              {[
                { title: 'Play', desc: 'Grind the arcade + Descent for free', icon: Star, color: '#fbbf24' },
                { title: 'Rank', desc: `Get ${minPoints}+ points to qualify`, icon: Shield, color: '#38bdf8' },
                { title: 'Get SOL', desc: 'Monthly payouts to top wallets', icon: Gift, color: '#7dd3fc' },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-2.5 mb-2.5 last:mb-0 relative z-10">
                  <div className="p-1 flex-shrink-0 mt-0.5" style={{ background: `${s.color}10`, border: `1px solid ${s.color}25` }}>
                    <s.icon className="w-3 h-3" style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="text-xs font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(180,230,200,0.8)' }}>{s.title}</p>
                    <p className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.45)' }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </Panel>

            {/* Discord */}
            <Panel className="p-4 mt-3">
              <a href="https://discord.gg/3wH7NaVXxP" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 relative z-10 group" data-testid="discord-cta">
                <Users className="w-4 h-4 group-hover:scale-110 transition-transform" style={{ color: '#7dd3fc' }} />
                <span className="text-xs font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: '#7dd3fc' }}>The Sewer Syndicate</span>
                <ExternalLink className="w-3 h-3 ml-auto" style={{ color: 'rgba(125,211,252,0.35)' }} />
              </a>
            </Panel>
          </div>
        </div>

        {/* ═══ FOOTER NOTE ═══ */}
        <div className="text-center py-4">
          <p className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(125,211,252,0.3)' }}>
            No purchase required. Play free · Top the monthly board · Get paid in SOL.
          </p>
        </div>
      </div>
    </div>
  );
}
